/**
 * Improved AI matching algorithm for the frontend
 * This file contains functions to improve the AI matching algorithm
 * to prevent non-business questions from matching with business-specific answers.
 */

/**
 * Checks if a question is business-related by looking for business keywords
 * @param {string} question - The user's question
 * @returns {boolean} - Whether the question is business-related
 */
export function isBusinessRelatedQuestion(question) {
  if (!question) return false;

  // Convert to lowercase for case-insensitive matching
  const lowerQuestion = question.toLowerCase().trim();

  // Define business-related keywords
  const businessKeywords = [
    'hour', 'open', 'close', 'time', 'schedule', 'day',
    'service', 'offer', 'provide', 'available',
    'cost', 'price', 'fee', 'charge', 'estimate', 'quote',
    'repair', 'fix', 'broke', 'broken', 'issue', 'problem',
    'appointment', 'schedule', 'book', 'reserve',
    'location', 'address', 'direction', 'where',
    'phone', 'call', 'contact', 'reach',
    'warranty', 'guarantee', 'insured', 'licensed',
    'ac', 'air', 'heat', 'cooling', 'hvac',
    'plumbing', 'pipe', 'drain', 'water', 'leak',
    'pest', 'bug', 'insect', 'rodent', 'termite',
    'sell', 'product', 'item', 'merchandise', 'inventory', 'stock',
    'what', 'do', 'you', 'business', 'company', 'store',
    'how', 'much', 'come', 'out', 'visit', 'trip', 'money'
  ];

  // Check if the question contains at least one business-related keyword
  return businessKeywords.some(keyword => lowerQuestion.includes(keyword));
}

/**
 * Finds the best matching answer with improved algorithm
 * @param {string} question - The user's question
 * @param {Array} commonQuestions - Array of Q&A pairs
 * @returns {string|null} - The matching answer or null if no match
 */
export function findBestMatchingAnswer(question, commonQuestions) {
  if (!question || !commonQuestions || !commonQuestions.length) {
    return null;
  }

  // First check if the question is business-related
  if (!isBusinessRelatedQuestion(question)) {
    console.log('Question does not contain any business-related keywords, using fallback');
    return null; // Return null to use fallback response
  }

  // Convert question to lowercase for case-insensitive matching
  const lowerQuestion = question.toLowerCase().trim();

  // Extract tokens from the user question
  const userTokens = lowerQuestion.split(/[\s,.?!;:()'"]+/).filter(t => t.length > 2);

  // Define intent keywords for better matching
  const intentKeywords = {
    'hours': ['hour', 'open', 'close', 'time', 'schedule', 'day'],
    'location': ['where', 'location', 'address', 'directions', 'located'],
    'pricing': ['price', 'cost', 'rate', 'fee', 'charge', 'much', 'dollar', 'service fee', 'service charge', 'come out', 'visit', 'trip', 'service call'],
    'appointment': ['appointment', 'schedule', 'book', 'reserve', 'set up', 'meet'],
    'repair': ['repair', 'fix', 'broke', 'broken', 'not working', 'issue', 'problem', 'ac', 'air condition'],
    'shipping': ['shipping', 'delivery', 'ship', 'deliver', 'sent', 'arrival', 'package', 'order status'],
    'products': ['sell', 'product', 'item', 'merchandise', 'inventory', 'stock', 'offer', 'what do you', 'do you sell']
  };

  // First, try direct match with question
  for (const qa of commonQuestions) {
    if (!qa.question || !qa.answer) continue;

    const lowerStoredQuestion = qa.question.toLowerCase().trim();
    if (lowerQuestion === lowerStoredQuestion) {
      console.log('Direct question match found:', qa.question);
      return qa.answer;
    }
  }

  // Scoring algorithm
  let bestMatch = null;
  let highestScore = 0;

  for (const qa of commonQuestions) {
    // Skip invalid entries
    if (!qa.question || !qa.answer) continue;

    let score = 0;
    const lowerStoredQuestion = qa.question.toLowerCase();

    // Check for intent keywords in both user question and stored question
    for (const [_intent, keywords] of Object.entries(intentKeywords)) {
      // Check if stored question relates to this intent
      const isStoredQuestionRelated = keywords.some(keyword =>
        lowerStoredQuestion.includes(keyword)
      );

      // If stored question relates to this intent, check if user question has matching keywords
      if (isStoredQuestionRelated) {
        for (const keyword of keywords) {
          if (lowerQuestion.includes(keyword)) {
            score += 3; // High score for intent match
            break; // Only count intent match once
          }
        }
      }
    }

    // Token-based matching
    const storedTokens = lowerStoredQuestion.split(/[\s,.?!;:()'"]+/).filter(t => t.length > 2);

    // Check each user token against stored tokens
    for (const token of userTokens) {
      if (storedTokens.includes(token)) {
        score += 2; // Medium score for token match
      }
      // Also check for substring match
      else if (storedTokens.some(t => t.includes(token) || token.includes(t))) {
        score += 1; // Lower score for partial token match
      }
    }

    // Bonus for question format
    if (lowerStoredQuestion.startsWith("what") ||
        lowerStoredQuestion.startsWith("when") ||
        lowerStoredQuestion.startsWith("where") ||
        lowerStoredQuestion.startsWith("how") ||
        lowerStoredQuestion.startsWith("is") ||
        lowerStoredQuestion.startsWith("are") ||
        lowerStoredQuestion.startsWith("do")) {
      score += 1; // Bonus for question format
    }

    console.log(`Score for "${qa.question}": ${score}`);

    // Update best match if current score is higher
    if (score > highestScore) {
      highestScore = score;
      bestMatch = qa.answer;
    }
  }

  console.log(`Highest score: ${highestScore}, Best match: ${bestMatch ? bestMatch.substring(0, 30) + '...' : 'null'}`);

  // IMPROVED MATCHING: Require both a minimum score AND a minimum percentage of matching tokens
  const minScore = 5; // Minimum score required (higher than the original threshold of 3)
  const minMatchPercentage = 25; // Reduced from 30% to 25% to be more lenient

  // Calculate the percentage of user tokens that matched with the best match
  let matchPercentage = 0;
  if (userTokens.length > 0 && bestMatch) {
    // Find the question that produced the best match
    const bestMatchQuestion = commonQuestions.find(qa => qa.answer === bestMatch);
    if (bestMatchQuestion) {
      const bestMatchTokens = bestMatchQuestion.question.toLowerCase().split(/[\s,.?!;:()'"]+/).filter(t => t.length > 2);

      // Count how many user tokens match with the best match tokens
      let matchingTokenCount = 0;
      for (const token of userTokens) {
        // Direct token match
        if (bestMatchTokens.includes(token)) {
          matchingTokenCount++;
          continue;
        }

        // Check for partial token matches (more lenient)
        for (const bestToken of bestMatchTokens) {
          // If token is part of a best token or vice versa
          if (bestToken.includes(token) || token.includes(bestToken)) {
            // Only count if the overlap is significant (at least 3 characters)
            const overlapLength = Math.min(token.length, bestToken.length);
            if (overlapLength >= 3) {
              matchingTokenCount += 0.5; // Partial match counts as half
              break;
            }
          }
        }
      }

      // Calculate the percentage
      matchPercentage = (matchingTokenCount / userTokens.length) * 100;

      // Special case for service/cost/fee questions
      const lowerBestQuestion = bestMatchQuestion.question.toLowerCase();

      // Check for service cost/fee related questions
      if ((lowerBestQuestion.includes('cost') || lowerBestQuestion.includes('fee') ||
           lowerBestQuestion.includes('charge') || lowerBestQuestion.includes('price') ||
           lowerBestQuestion.includes('service call')) &&
          (lowerQuestion.includes('cost') || lowerQuestion.includes('fee') ||
           lowerQuestion.includes('charge') || lowerQuestion.includes('price') ||
           lowerQuestion.includes('how much') || lowerQuestion.includes('service'))) {
        // Boost the match percentage for service cost questions
        matchPercentage = Math.max(matchPercentage, 30);
        console.log('Applied service cost question boost');
      }

      // Special case for "come out" or "visit" phrases
      if ((lowerBestQuestion.includes('come out') || lowerBestQuestion.includes('visit') ||
           lowerBestQuestion.includes('service call')) &&
          (lowerQuestion.includes('come out') || lowerQuestion.includes('visit') ||
           lowerQuestion.includes('come to') || lowerQuestion.includes('trip') ||
           lowerQuestion.includes('service call'))) {
        // Boost the match percentage for service visit questions
        matchPercentage = Math.max(matchPercentage, 30);
        console.log('Applied service visit question boost');
      }

      // Special case for "how much" questions
      if ((lowerBestQuestion.includes('cost') || lowerBestQuestion.includes('fee') ||
           lowerBestQuestion.includes('charge') || lowerBestQuestion.includes('price')) &&
          (lowerQuestion.includes('how much') ||
           (lowerQuestion.includes('how') && lowerQuestion.includes('much')))) {
        // Boost the match percentage for "how much" questions
        matchPercentage = Math.max(matchPercentage, 30);
        console.log('Applied "how much" question boost');
      }
    }
  }

  console.log(`Match percentage: ${matchPercentage.toFixed(2)}% (minimum required: ${minMatchPercentage}%)`);

  // Return best match only if BOTH the score is high enough AND the match percentage is high enough
  if (highestScore >= minScore && matchPercentage >= minMatchPercentage && bestMatch) {
    console.log(`Found match for question with score ${highestScore} and match percentage ${matchPercentage.toFixed(2)}%`);
    return bestMatch;
  } else {
    console.log(`No good match found, using fallback response`);
    return null; // Return null to use fallback response
  }
}

/**
 * Gets the fallback message to use when no good match is found
 * @returns {string} - The fallback message
 */
export function getFallbackMessage() {
  return "I'll give you a call shortly to discuss this in more detail. Thank you for reaching out!";
}
